/**
 * The X Social Review rubric, in the reader's language.
 *
 * Every constant here mirrors `pipeline/x_social_review/score.py` in envoz-editorial. It is
 * duplicated on purpose: the pipeline persists the *outcome* of the rubric (grades, penalties,
 * gates) but never the rubric itself, and an editor looking at a grade of 3 has no way to know
 * what 3 means without it. The `satisfies Record<Union, …>` on each map is the guard — the
 * pipeline adding a dimension or a penalty type fails `tsc` here until someone writes down what
 * it means, instead of the dashboard silently rendering a bare number.
 *
 * If the weights or thresholds change in score.py, they change here in the same commit.
 */

import type { EditorialWatchStatus, SocialClassification, XConfidence, XDimension, XPenaltyType } from 'src/types/editorial';

/** Grades are integers 0–4 on every dimension. */
export const GRADE_MAX = 4;

export type DimensionRubric = {
  label: string;
  /** Points this dimension contributes at grade 4. The six weights sum to 100. */
  weight: number;
  /** What the dimension is measuring, in one line. */
  measures: string;
  /** What each grade means, index 0–4. */
  grades: [string, string, string, string, string];
};

export const DIMENSION_RUBRIC = {
  story_match: {
    label: 'Correspondencia con la historia',
    weight: 30,
    measures: 'Si la conversación encontrada habla del mismo hecho, no de un tema parecido.',
    grades: [
      'Ninguna publicación habla del hecho.',
      'Coincide el tema general, no el hecho.',
      'Algunas publicaciones tratan el hecho; otras se desvían.',
      'La mayoría trata el hecho concreto.',
      'Todas tratan el mismo hecho, con detalles verificables en común.',
    ],
  },
  conversation_breadth: {
    label: 'Amplitud de la conversación',
    weight: 25,
    measures: 'Cuánta gente distinta participa, no cuántas veces se repite la misma.',
    grades: [
      'Sin conversación.',
      'Una o dos cuentas aisladas.',
      'Un grupo pequeño y homogéneo.',
      'Varios círculos distintos participan.',
      'Conversación amplia y transversal entre comunidades.',
    ],
  },
  participant_authority: {
    label: 'Autoridad de quien participa',
    weight: 20,
    measures: 'Quién habla: cuentas oficiales, medios y especialistas pesan más que público general.',
    grades: [
      'Solo cuentas anónimas o sin contexto.',
      'Público general únicamente.',
      'Alguna cuenta con audiencia relevante.',
      'Medios, periodistas o especialistas participan.',
      'Cuentas oficiales o de autoridad reconocida participan directamente.',
    ],
  },
  narrative_richness: {
    label: 'Riqueza narrativa',
    weight: 15,
    measures: 'Si hay ángulos, posturas y matices distintos, o una sola frase repetida.',
    grades: [
      'Sin contenido interpretable.',
      'Una sola frase repetida.',
      'Una narrativa dominante, sin contraste.',
      'Dos o más narrativas en tensión.',
      'Debate con posturas, matices y datos aportados.',
    ],
  },
  momentum: {
    label: 'Momento',
    weight: 5,
    measures: 'Si la conversación está creciendo ahora o ya se apagó.',
    grades: [
      'Sin actividad.',
      'Actividad residual y antigua.',
      'Actividad estable, sin crecimiento.',
      'Creciendo en las últimas horas.',
      'Aceleración clara y sostenida.',
    ],
  },
  observable_engagement: {
    label: 'Interacción observable',
    weight: 5,
    measures: 'Likes, reposts y respuestas visibles en las publicaciones citadas.',
    grades: [
      'Sin interacción visible.',
      'Interacción mínima.',
      'Interacción moderada.',
      'Interacción alta en varias publicaciones.',
      'Interacción muy alta y distribuida.',
    ],
  },
} satisfies Record<XDimension, DimensionRubric>;

/** Declaration order for display — heaviest dimension first, matching how the score is built. */
export const DIMENSION_ORDER = [
  'story_match',
  'conversation_breadth',
  'participant_authority',
  'narrative_richness',
  'momentum',
  'observable_engagement',
] as const satisfies readonly XDimension[];

export type PenaltyRubric = {
  label: string;
  /** Negative points. Applied to the base score, floored at PENALTY_FLOOR in total. */
  points: number;
  meaning: string;
};

export const PENALTY_RUBRIC = {
  event_mismatch: {
    label: 'Hecho equivocado',
    points: -20,
    meaning: 'Las publicaciones tratan otro evento, aunque el tema se parezca.',
  },
  homonym_or_topic_collision: {
    label: 'Colisión de nombre o tema',
    points: -20,
    meaning: 'La búsqueda arrastró otro suceso o persona con nombre parecido.',
  },
  single_account_dominance: {
    label: 'Una sola cuenta domina',
    points: -10,
    meaning: 'Casi toda la conversación viene de una cuenta: no es conversación, es difusión.',
  },
  repeated_account_content: {
    label: 'Contenido repetido',
    points: -5,
    meaning: 'Las mismas cuentas repiten el mismo texto.',
  },
  suspected_coordination: {
    label: 'Posible coordinación',
    points: -10,
    meaning: 'Patrón de publicación que sugiere amplificación coordinada, no interés orgánico.',
  },
  stale_conversation: {
    label: 'Conversación vieja',
    points: -10,
    meaning: 'La actividad es anterior a la ventana de análisis; ya pasó.',
  },
  insufficient_evidence: {
    label: 'Evidencia insuficiente',
    points: -10,
    meaning: 'Se encontraron muy pocas publicaciones utilizables para sostener una lectura.',
  },
} satisfies Record<XPenaltyType, PenaltyRubric>;

/** Total penalty never exceeds this, however many fire. */
export const PENALTY_FLOOR = -30;

/**
 * Two penalty pairs are alternatives, not additions — only the harsher of each pair is charged.
 * Without this note the arithmetic on screen looks wrong when both appear in the list.
 */
export const PENALTY_EXCLUSIVE_GROUPS: ReadonlyArray<readonly XPenaltyType[]> = [
  ['event_mismatch', 'homonym_or_topic_collision'],
  ['single_account_dominance', 'repeated_account_content'],
];

/** Score bands, before gates. Read as: `>= min` and below the next band's min. */
export const CLASSIFICATION_BANDS = [
  { classification: 'breaking_candidate', min: 90 },
  { classification: 'high', min: 80 },
  { classification: 'relevant', min: 60 },
  { classification: 'emerging', min: 45 },
  { classification: 'irrelevant', min: 0 },
] as const satisfies ReadonlyArray<{ classification: SocialClassification; min: number }>;

/**
 * Gates run *after* the band is chosen and can only lower it. The pipeline records the ones that
 * fired as opaque strings; this maps each to why it exists, so a story that scored 84 but reads
 * "Relevante" has a stated reason instead of looking like a bug.
 */
export const GATE_EXPLANATION: Record<string, string> = {
  'story_match<=1→irrelevant':
    'La correspondencia con la historia fue 1 o menos: la conversación no trata este hecho, así que ningún otro puntaje cuenta.',
  'story_match==2→max_emerging':
    'La correspondencia fue 2: hay relación parcial con el hecho, y eso topa la clasificación en Emergente.',
  'breadth<2_or_authority<2→not_high':
    'Sin amplitud ni autoridad suficientes no se sostiene una clasificación Alta, aunque el puntaje llegue.',
  'momentum<3→not_breaking_candidate':
    'Un posible breaking exige que la conversación esté creciendo ahora; el momento no alcanzó 3.',
  'insufficient_evidence→not_high':
    'Se marcó evidencia insuficiente: no hay suficientes publicaciones para sostener una clasificación Alta.',
};

export const CONFIDENCE_LABEL = {
  high: 'Confianza alta',
  medium: 'Confianza media',
  low: 'Confianza baja',
} satisfies Record<XConfidence, string>;

export const CONFIDENCE_MEANING = {
  high: 'Correspondencia perfecta, 8+ publicaciones, 5+ cuentas distintas de 3+ tipos, y casi todas con URL, fecha y texto.',
  medium: 'Correspondencia de 3 o más, al menos 5 publicaciones y 3 cuentas distintas.',
  low: 'No se alcanzó el mínimo de publicaciones, cuentas o correspondencia para sostener una lectura firme.',
} satisfies Record<XConfidence, string>;

/** Points this dimension contributed to the base score: `(grade / 4) × weight`. */
export function dimensionContribution(dimension: XDimension, grade: number): number {
  return (grade / GRADE_MAX) * DIMENSION_RUBRIC[dimension].weight;
}

export function isKnownDimension(name: string): name is XDimension {
  return name in DIMENSION_RUBRIC;
}

export function isKnownPenalty(type: string): type is XPenaltyType {
  return type in PENALTY_RUBRIC;
}

/** The band a raw score falls in, before gates. Lets the UI show the gate's effect as a drop. */
export function bandForScore(score: number): SocialClassification {
  return (CLASSIFICATION_BANDS.find(band => score >= band.min) ?? CLASSIFICATION_BANDS.at(-1)!).classification;
}

// ── relevance watches ────────────────────────────────────────────────────────

/**
 * The score at which the pipeline acts: only a story at or above it is auto-transformed into a
 * draft. Mirrors `editorial_min_x_score` in the pipeline's settings — a number the API does not
 * publish, and the reference line every relevance curve is read against.
 *
 * Keep the two in sync. A curve drawn against the wrong threshold would say a story was
 * actionable when the drafter ignored it.
 */
export const ACTION_THRESHOLD = 65;

/** Where tracking starts: `relevance_watch_min_score`. Below it there is no conversation to trace. */
export const WATCH_FLOOR = 10;

/**
 * How a watch ended, in the editor's terms.
 *
 * Deliberately five distinct sentences rather than "cerrado": which of them happened is the whole
 * finding. A story that peaked and collapsed and one whose window simply ran out look identical
 * on the curve's last point and mean opposite things.
 */
export const WATCH_STATUS_LABEL = {
  watching: 'en seguimiento',
  collapsed: 'subió y colapsó',
  faded: 'se apagó',
  completed: 'seguimiento completo',
  expired: 'ventana cerrada',
} satisfies Record<EditorialWatchStatus, string>;

export const WATCH_STATUS_MEANING = {
  watching: 'La ventana sigue abierta: puede haber más lecturas.',
  collapsed: 'Pasó el umbral de acción y luego cayó a ruido. Se dejó de medir porque ya respondió la pregunta.',
  faded: 'Nunca subió y cayó debajo del piso de seguimiento. No valía otra lectura pagada.',
  completed: 'Gastó sus relecturas dentro de la ventana y seguía vivo.',
  expired: 'Se cerraron las cinco horas con relecturas sin gastar: la caja no estaba capturando para tomarlas.',
} satisfies Record<EditorialWatchStatus, string>;

/** Where a reading came from. A curve de cinco relecturas pagadas no es la misma evidencia que una
 *  sola medición inicial, y el origen es lo único que lo dice. */
export const WATCH_SAMPLE_SOURCE_LABEL: Record<string, string> = {
  x_review: 'medición inicial',
  resample: 'relectura',
};
